import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SearchOverlay from "@/components/SearchOverlay";

export const LOGO_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_898301c9-3ad4-4fc7-8ea1-ebcc4eaa0b3a/artifacts/8qu6g6ot_GAVRA%20LOGO%20Transparent%20for%20Website.png";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/products", label: "Products" },
  { to: "/artists", label: "Artists" },
  { to: "/dealers", label: "Dealers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/80 backdrop-blur-md border-b border-black/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="flex h-20 items-center justify-between">
            <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
              <img src={LOGO_URL} alt="GAVRA" className="h-12 md:h-16 w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-10">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `link-underline text-[13px] font-bold uppercase tracking-[0.15em] py-1 ${
                      isActive ? "text-gavra" : "text-black"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3 md:gap-5">
              <button
                data-testid="open-search-btn"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 text-black hover:text-gavra transition-colors"
              >
                <Search className="h-5 w-5" strokeWidth={2.5} />
                <span className="hidden md:inline text-[13px] font-bold uppercase tracking-[0.15em]">Search</span>
              </button>
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setMobileOpen((v) => !v)}
                className="lg:hidden text-black"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed top-20 left-0 right-0 z-40 bg-white border-b border-black/10 lg:hidden"
          >
            <div className="flex flex-col px-5 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `py-3 text-2xl font-heading font-extrabold uppercase tracking-tight ${
                      isActive ? "text-gavra" : "text-black"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
